/* eslint-disable camelcase */
import { SelectOption } from 'components/inputs/BaseSelect';
import Immutable from 'immutable';
import { Color } from 'records/Color';
import { Permissions, RawPermissions } from 'records/Permissions';
import { PunchStatus, PunchStatusValue } from 'records/Punch';
import { UpdateProject } from 'records/UpdateProject';
import { RawUser, User } from './User';

export type ProjectId = string;

export const ProjectTypeCivil = 'Civil / Infrastructure';
export const ProjectTypeSports = 'Sports / Entertainment / Recreation';
export const ProjectTypeHealthcare = 'Healthcare';
export const ProjectTypeTI = 'Tenant Improvement';
export const ProjectTypeRES = 'Residential';
export const ProjectTypeOR = 'Office / Retail';
export const ProjectTypeUTIL = 'Utilities';
export const ProjectTypeOG = 'Oil & Gas';
export const ProjectTypeOther = 'Other';

export const BudgetUnset = 'Unset';
export const Budget0_5 = '$0 - $5M';
export const Budget5_10 = '$5M - $10M';
export const Budget10_20 = '$10M - $20M';
export const Budget20_50 = '$20M - $50M';
export const Budget50_100 = '$50M - $100M';
export const Budget100_500 = '$100M - $500M';
export const Budget500Plus = '$500M+';

export enum ProjectTypeEnum {
  EMPTY = '',
  UNKNOWN = 'PROJECT_TYPE_UNKNOWN',
  CIVIL = 'PROJECT_TYPE_CIVIL',
  SPORTS_ET = 'PROJECT_TYPE_SPORTS_ET',
  HEALTHCARE = 'PROJECT_TYPE_HEALTHCARE',
  TI = 'PROJECT_TYPE_TI',
  RES = 'PROJECT_TYPE_RES',
  OR = 'PROJECT_TYPE_OR',
  UTIL = 'PROJECT_TYPE_UTIL',
  OG = 'PROJECT_TYPE_OG',
  OTHER = `PROJECT_TYPE_OTHER`,
}

export interface ProjectType {
  display: string;
  kind: ProjectTypeEnum;
}

export interface Priority extends SelectOption<string> {
  color: string;
}

export function projectTypeForEnum(e: ProjectTypeEnum): string {
  switch (e) {
    case ProjectTypeEnum.UNKNOWN:
      return 'Unknown';
    case ProjectTypeEnum.CIVIL:
      return ProjectTypeCivil;
    case ProjectTypeEnum.SPORTS_ET:
      return ProjectTypeSports;
    case ProjectTypeEnum.HEALTHCARE:
      return ProjectTypeHealthcare;
    case ProjectTypeEnum.TI:
      return ProjectTypeTI;
    case ProjectTypeEnum.RES:
      return ProjectTypeRES;
    case ProjectTypeEnum.OR:
      return ProjectTypeOR;
    case ProjectTypeEnum.UTIL:
      return ProjectTypeUTIL;
    case ProjectTypeEnum.OG:
      return ProjectTypeOG;
    case ProjectTypeEnum.OTHER:
      return ProjectTypeOther;
    default:
      return '';
  }
}

export enum ProjectBudgetEnum {
  EMPTY = '',
  UNSET = 'BUDGET_UNSET',
  BUDGET_0_5 = 'BUDGET_0_5',
  BUDGET_5_10 = 'BUDGET_5_10',
  BUDGET_10_20 = 'BUDGET_10_20',
  BUDGET_20_50 = 'BUDGET_20_50',
  BUDGET_50_100 = 'BUDGET_50_100',
  BUDGET_100_500 = 'BUDGET_100_500',
  BUDGET_500Plus = 'BUDGET_500_PLUS',
}

export interface ProjectBudget {
  display: string;
  kind: ProjectBudgetEnum;
}

export function budgetForEnum(b: ProjectBudgetEnum): string {
  switch (b) {
    case ProjectBudgetEnum.UNSET:
      return '';
    case ProjectBudgetEnum.BUDGET_0_5:
      return Budget0_5;
    case ProjectBudgetEnum.BUDGET_5_10:
      return Budget5_10;
    case ProjectBudgetEnum.BUDGET_10_20:
      return Budget10_20;
    case ProjectBudgetEnum.BUDGET_20_50:
      return Budget20_50;
    case ProjectBudgetEnum.BUDGET_50_100:
      return Budget50_100;
    case ProjectBudgetEnum.BUDGET_100_500:
      return Budget100_500;
    case ProjectBudgetEnum.BUDGET_500Plus:
      return Budget500Plus;
    default:
      return '';
  }
}

export interface ProjectLocation {
  center: Coordinate;
  bounds: Bounds | [];
}

export interface ProjectGeoLocation {
  center: number[];
  bbox: number[];
}

export interface RawProject {
  id: ProjectId;
  name: string;
  disciplines: string[];
  assignees: string[];
  statuses: string[];
  priorities: string[];
  permissions: RawPermissions;
  createdBy: string;
  createdDate: number;
  code: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
  geolocation: ProjectGeoLocation;
  user: RawUser;
  Status: string;
  creatorUser: RawUser;
  projectType: ProjectTypeEnum;
  budget: ProjectBudgetEnum;
  trialDaysRemaining: number;
  closeddate: number;
}

export interface ProjectData {
  id: ProjectId;
  name: string;
  disciplines: Immutable.OrderedSet<string>;
  assignees: Immutable.OrderedSet<string>;
  statuses: Immutable.OrderedSet<PunchStatus>; // TODO: Change this to Map<StatusKey, PunchStatus>
  priorities: Immutable.Map<string, Priority>;
  permissions: Permissions;
  createdBy: string;
  createdDate: number;
  code: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
  geolocation: ProjectGeoLocation;
  user: User;
  Status: string;
  creatorUser: User;
  projectType: ProjectTypeEnum;
  budget: ProjectBudgetEnum;
  trialDaysRemaining: number;
  location: ProjectLocation;
  closeddate: number;
}

const USA_CENTER: Coordinate = { longitude: -100.423087, latitude: 39.182295 };
const USA_BOUNDS: Bounds = [
  [-127.85734009, 17.49454069],
  [-67.7846607, 57.1438188],
];
const defaultProjectLocation = {
  center: USA_CENTER,
  bounds: USA_BOUNDS,
};

const defaultProjectGeoLocation = {
  center: [],
  bbox: [],
};

const defaultValues: ProjectData = {
  id: '',
  name: '',
  disciplines: Immutable.OrderedSet<string>(),
  assignees: Immutable.OrderedSet<string>(),
  statuses: Immutable.OrderedSet<PunchStatus>(),
  priorities: Immutable.Map<string, Priority>(),
  permissions: new Permissions(),
  createdBy: '',
  createdDate: 0,
  code: '',
  street: '',
  city: '',
  state: '',
  zipcode: '',
  user: new User(),
  Status: '',
  creatorUser: new User(),
  projectType: ProjectTypeEnum.UNKNOWN,
  budget: ProjectBudgetEnum.UNSET,
  trialDaysRemaining: 0,
  location: defaultProjectLocation,
  geolocation: defaultProjectGeoLocation,
  closeddate: 0,
};

export const ProjectFactory = Immutable.Record(defaultValues, 'Project');

export type ProjectMap = Immutable.OrderedMap<ProjectId, Project>;

export type ProjectList = Immutable.List<Project>;

const red = Color.fromRGB(0.8901961, 0.16078432, 0.25882354);
const orange = Color.fromRGB(0.99607843, 0.57254905, 0.007843138);
const gray = Color.fromRGB(0.8627451, 0.8745098, 0.8901961);

function statusToIssueStatus(
  statuses: string[]
): Immutable.OrderedSet<PunchStatus> {
  // TODO: Remove once API is updated
  statuses = statuses.map((x) => x.toUpperCase().split(' ').join('_'));

  let issuesStatuses = Immutable.OrderedSet<PunchStatus>();

  statuses.forEach((status) => {
    switch (status) {
      case PunchStatusValue.OPEN: {
        const open: PunchStatus = {
          status: PunchStatusValue.OPEN,
          displayString: 'OPEN',
          statusColor: red,
        };
        issuesStatuses = issuesStatuses.add(open);
        break;
      }
      case PunchStatusValue.CLOSED: {
        const closed: PunchStatus = {
          status: PunchStatusValue.CLOSED,
          displayString: 'CLOSED',
          statusColor: gray,
        };
        issuesStatuses = issuesStatuses.add(closed);
        break;
      }
      case PunchStatusValue.READY: {
        const ready: PunchStatus = {
          status: PunchStatusValue.READY,
          displayString: 'READY FOR REVIEW',
          statusColor: orange,
        };
        issuesStatuses = issuesStatuses.add(ready);
        break;
      }
      case PunchStatusValue.OUT_OF_SCOPE: {
        const outOfScope: PunchStatus = {
          status: PunchStatusValue.OUT_OF_SCOPE,
          displayString: 'OUT OF SCOPE',
          statusColor: gray,
        };
        issuesStatuses = issuesStatuses.add(outOfScope);
        break;
      }
      default:
        break;
    }
  });
  return issuesStatuses;
}

const priorityColors: any = {
  Hot: '#ee1d24',
  High: '#a73f8e',
  Normal: '',
  Low: '',
};

function formatPriorities(
  priorities: string[]
): Immutable.Map<string, Priority> {
  if (priorities) {
    return priorities.reduce((map, priority) => {
      return map.set(priority, {
        label: priority,
        value: priority,
        color: priorityColors[priority],
      });
    }, Immutable.Map<string, Priority>());
  }
  return Immutable.Map<string, Priority>();
}

export class Project extends ProjectFactory {
  static deserialize(
    rawProject: RawProject,
    isBasicOrUnpaidTier = false
  ): Project {
    return new Project({
      ...rawProject,
      disciplines: Immutable.OrderedSet<string>(rawProject.disciplines),
      assignees: Immutable.OrderedSet<string>(rawProject.assignees),
      statuses: statusToIssueStatus(rawProject.statuses),
      priorities: formatPriorities(rawProject.priorities),
      permissions: Permissions.deserialize(
        rawProject.permissions,
        isBasicOrUnpaidTier
      ),
      user: User.deserialize(rawProject.user),
      creatorUser: User.deserialize(rawProject.creatorUser),
    });
  }

  serialize(): RawProject {
    return this.toJS();
  }

  intoUpdateProject(): UpdateProject {
    return new UpdateProject({
      ...this.toJS(),
      projectid: this.id,
    });
  }
}
